/*
 * Reading numbers people type into the calculators.
 *
 * The inputs are type="text" with inputMode="decimal" (no spinners, and a
 * numeric input in a comma-decimal locale can hand back a value bare
 * parseFloat truncates), so separators are resolved here.
 *
 * The hard case is a single separator followed by exactly three digits:
 * "5.000" is five in English and five thousand in Chile, Ecuador or Spain;
 * "1,250" is one thousand two hundred and fifty in English and 1.25 in those
 * countries. Guessing wrong is a factor of a thousand, so it is never a
 * silent guess:
 *
 *   - The separator the reader's locale uses for thousands is read as
 *     thousands ("5.000" is 5000 in es-CL, "1,250" is 1250 in en-AU).
 *   - A comma in a comma-decimal locale is read as the decimal ("1,250" is
 *     1.25 in es-CL): people who write decimal commas never group with them.
 *   - A point in a point-decimal locale is AMBIGUOUS and refused. English
 *     writers seldom type "5.000" to mean five, while comma-decimal writers
 *     on English-language devices type it to mean five thousand. The user is
 *     asked to type 5 or 5000 instead (numberInputIssue).
 *
 * Everything else is unambiguous: two or more of the same separator is
 * digit grouping ("5.000.000", "1,000,000"); both separators means the last
 * one is the decimal ("1.500,5", "1,500.5"); a leading zero or empty whole
 * part cannot be grouping ("0.500", ".750"); any digit count other than three
 * after a single separator is a decimal ("1.5", "1,25", "2.0000").
 *
 * Text after the number is ignored, as parseFloat always did ("12 m" is 12).
 */

/*
 * Stamped on every calculator result as inputReader, so a result read by these
 * rules is never mistaken for one read by the old reader (calcVersions.js).
 * 1 is the reader before this module; bump when a typed value changes meaning.
 */
export const INPUT_READER_VERSION = 2;

/** The decimal separator of the reader's locale: ',' or '.'. */
export function localeDecimalSeparator() {
  try {
    const nav = typeof navigator !== 'undefined' ? navigator : null;
    const locale = (nav && ((nav.languages && nav.languages[0]) || nav.language)) || 'en';
    const part = new Intl.NumberFormat(locale).formatToParts(1.1).find((p) => p.type === 'decimal');
    return part && part.value === ',' ? ',' : '.';
  } catch {
    return '.';
  }
}

let cachedDecimal = null;
const defaultDecimal = () => {
  if (cachedDecimal === null) cachedDecimal = localeDecimalSeparator();
  return cachedDecimal;
};

// Whitespace of every kind is digit grouping ("1 500", "1 500"), so it goes.
const clean = (v) => String(v ?? '').trim().replace(/\s/g, '');

/*
 * Split a typed value into sign, the number as typed, and how to read it.
 * Returns { value, ambiguous } where ambiguous carries both readings.
 */
function read(v, decimal) {
  if (typeof v === 'number') return { value: Number.isFinite(v) ? v : NaN };
  const s = clean(v);
  const m = s.match(/^([-+−]?)([\d.,]+)/);
  if (!m) return { value: NaN };
  const sign = m[1] === '-' || m[1] === '−' ? -1 : 1;
  const t = m[2];
  const num = (digits) => {
    const x = parseFloat(digits);
    return Number.isFinite(x) ? sign * x : NaN;
  };
  const dots = (t.match(/\./g) || []).length;
  const commas = (t.match(/,/g) || []).length;
  if (!dots && !commas) return { value: num(t) };

  if (dots && commas) {
    // The last separator is the decimal and may appear only once.
    const dec = t.lastIndexOf(',') > t.lastIndexOf('.') ? ',' : '.';
    const grp = dec === ',' ? '.' : ',';
    const [whole, frac, extra] = t.split(dec);
    if (extra !== undefined || frac === undefined || frac.includes(grp)) return { value: NaN };
    if (!new RegExp(`^\\d{1,3}(\\${grp}\\d{3})*$`).test(whole)) return { value: NaN };
    return { value: num(whole.split(grp).join('') + '.' + frac) };
  }

  const sep = dots ? '.' : ',';
  const count = dots || commas;
  if (count > 1) {
    // Only digit grouping repeats a separator, and only in threes.
    if (!new RegExp(`^\\d{1,3}(\\${sep}\\d{3})+$`).test(t)) return { value: NaN };
    return { value: num(t.split(sep).join('')) };
  }

  const [whole, frac] = t.split(sep);
  const asDecimal = num(`${whole || '0'}.${frac || '0'}`);
  const couldBeThousands = frac.length === 3 && /^[1-9]\d{0,2}$/.test(whole);
  if (!couldBeThousands) return { value: asDecimal };
  const asThousands = num(whole + frac);
  if (sep !== decimal) return { value: asThousands };
  if (sep === ',') return { value: asDecimal };
  return { value: NaN, ambiguous: { typed: String(v).trim(), asDecimal, asThousands } };
}

/** The number a typed value means, or NaN if it is not one or is ambiguous. */
export function parseNumberInput(v, decimal = defaultDecimal()) {
  return read(v, decimal).value;
}

/**
 * Why a typed value was refused, when the reason is worth telling the user.
 * Null for a readable number, an empty field, or plain non-numbers.
 */
export function numberInputIssue(v, decimal = defaultDecimal()) {
  if (typeof v !== 'string' || !v.trim()) return null;
  const r = read(v, decimal);
  return r.ambiguous ? { kind: 'ambiguous', ...r.ambiguous } : null;
}

/** What to tell the user about an ambiguous value. English; plain digits. */
export function ambiguousNumberMessage(label, issue) {
  const plain = (x) => String(x);
  return `${label}: "${issue.typed}" could mean ${plain(issue.asDecimal)} or ${plain(issue.asThousands)}. Type ${plain(issue.asDecimal)} or ${plain(issue.asThousands)} instead.`;
}

/*
 * The reader used before this module existed, kept only so a result saved
 * under it can be checked: if a saved input means something different now,
 * the saved figures were computed from a misread number (calcVersions.js).
 * Do not use it to read new input.
 */
export function legacyParseNumber(v) {
  if (typeof v === 'number') return Number.isFinite(v) ? v : NaN;
  let s = String(v ?? '').trim().replace(/\s/g, '');
  if (!s) return NaN;
  const lastComma = s.lastIndexOf(',');
  const lastDot = s.lastIndexOf('.');
  if (lastComma !== -1 && lastDot !== -1) {
    s = lastComma > lastDot ? s.replace(/\./g, '').replace(',', '.') : s.replace(/,/g, '');
  } else if (lastComma !== -1) {
    s = /,\d{1,2}$/.test(s) ? s.replace(',', '.') : s.replace(/,/g, '');
  }
  const x = parseFloat(s);
  return Number.isFinite(x) ? x : NaN;
}
